import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Card } from "../components/ui/card";
import { Lock, ShieldCheck, Loader2 } from "lucide-react";
import { toast } from "sonner";

export default function Register() {
  const { register, formatApiErrorDetail } = useAuth();
  const nav = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    setLoading(true);
    try {
      await register(name, email, password);
      toast.success("Account created — let's build your resume.");
      nav("/dashboard");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50 px-4 py-12 bg-grid">
      <div className="w-full max-w-md">
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-9 h-9 rounded-lg bg-slate-900 text-white flex items-center justify-center">
            <Lock className="w-4 h-4" />
          </div>
          <span className="font-display font-bold text-xl tracking-tight">ResumeLock</span>
        </Link>
        <Card className="p-8 shadow-sm">
          <h1 className="font-display text-2xl font-bold tracking-tight">Create your free account</h1>
          <p className="text-sm text-slate-500 mt-1">Your data is encrypted before it hits our database.</p>

          <form onSubmit={submit} className="mt-6 space-y-4">
            <div>
              <Label htmlFor="name" className="text-sm font-medium">Name</Label>
              <Input
                id="name" required value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Jordan Rivera"
                data-testid="register-name-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="email" className="text-sm font-medium">Email</Label>
              <Input
                id="email" type="email" required value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                data-testid="register-email-input"
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="password" className="text-sm font-medium">Password</Label>
              <Input
                id="password" type="password" required value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Min. 8 characters"
                data-testid="register-password-input"
                className="mt-1.5"
              />
              <p className="text-xs text-slate-500 mt-1.5">Stored using bcrypt — we never see your plain password.</p>
            </div>
            <Button type="submit" disabled={loading} className="w-full bg-slate-900 hover:bg-slate-800 h-11" data-testid="register-submit-btn">
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create account"}
            </Button>
          </form>

          <div className="mt-6 text-center text-sm text-slate-500">
            Already have an account? <Link to="/login" className="text-blue-600 font-medium hover:underline" data-testid="register-login-link">Sign in</Link>
          </div>
        </Card>
        <div className="mt-6 flex items-center justify-center gap-2 text-xs text-slate-500">
          <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" />
          AES-encrypted resume storage included on every account.
        </div>
      </div>
    </div>
  );
}
